// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file onboarding.service.ts
// @author Arlinda Peixoto
// @company Pagali
// @description Serviço para gestão de telas de onboarding
// @date 2026-01-18
// ------------------------------------------------------------

import api from './api';

export interface OnboardingScreen {
  id: string;
  ordem: number;
  titulo: string;
  descricao?: string;
  temImagem?: boolean;
  imagemUrl?: string;
  mimeType?: string;
  corFundo: string;
  corTitulo: string;
  corDescricao: string;
  botaoTexto: string;
  botaoCor: string;
  plataforma: 'MOBILE' | 'WEB';
  estado: 'A' | 'I';
  dataRegisto?: string;
  dataAtualizacao?: string;
}

export interface OnboardingInput {
  titulo: string;
  descricao?: string;
  ordem?: number;
  imagemBase64?: string;
  imagemUrl?: string;
  mimeType?: string;
  corFundo?: string;
  corTitulo?: string;
  corDescricao?: string;
  botaoTexto?: string;
  botaoCor?: string;
  plataforma?: 'MOBILE' | 'WEB';
  estado?: 'A' | 'I';
}

export interface OnboardingStatusResponse {
  success: boolean;
  tableExists: boolean;
}

export interface OnboardingListResponse {
  success: boolean;
  data: OnboardingScreen[];
  total: number;
  needsMigration?: boolean;
}

export const onboardingService = {
  // Verificar estado da tabela
  async checkStatus(): Promise<OnboardingStatusResponse> {
    const response = await api.get('/admin/onboarding/status');
    return response.data;
  },

  // Executar migração (criar tabela)
  async migrate(): Promise<{ success: boolean; message: string; created: boolean }> {
    const response = await api.post('/admin/onboarding/migrate');
    return response.data;
  },

  // Listar telas de onboarding
  async list(params?: {
    estado?: string;
    plataforma?: string;
  }): Promise<OnboardingListResponse> {
    const response = await api.get('/admin/onboarding', { params });
    return response.data;
  },

  // Obter detalhes de uma tela
  async getById(id: string): Promise<{ success: boolean; data: OnboardingScreen }> {
    const response = await api.get(`/admin/onboarding/${id}`);
    return response.data;
  },

  // Criar nova tela
  async create(data: OnboardingInput): Promise<{ success: boolean; data: { id: string; ordem: number } }> {
    const response = await api.post('/admin/onboarding', data);
    return response.data;
  },

  // Atualizar tela
  async update(id: string, data: Partial<OnboardingInput>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/onboarding/${id}`, data);
    return response.data;
  },

  // Reordenar telas
  async reorder(items: { id: string; ordem: number }[]): Promise<{ success: boolean; message: string }> {
    const response = await api.put('/admin/onboarding/reorder', { items });
    return response.data;
  },

  // Eliminar tela
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/onboarding/${id}`);
    return response.data;
  },

  // Obter URL da imagem
  getImageUrl(id: string): string {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
    return `${baseUrl}/v1/admin/onboarding/${id}/image`;
  },
};

export default onboardingService;
